/**
 * PageContextProvider — wires the parent-page bridge to the WunderGuide
 * backend when the partner has page-context capture enabled.
 *
 * Responsibilities:
 *   1. Forward `instance.page_capture` to the bootstrap (`wg:capabilities`)
 *      so the bootstrap knows whether to observe / dispatch / which routes.
 *   2. Listen to bootstrap-emitted page models and PATCH them onto the
 *      live LiveKit session (`/sessions/v1/{sid}`) so the agent's
 *      synthesizer can read browsing context as personalization.
 *   3. Expose `proposeAction(verb, args)` that consults auto-grant rules,
 *      surfaces a non-blocking toast on auto-granted actions, and
 *      otherwise returns to the caller for explicit user confirmation
 *      (rendered elsewhere — this provider only owns the dispatch).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { httpClient } from '../client';
import logger from '../utils/logger';
import { useInstance } from './InstanceContext';
import { useChatSession } from './ChatSessionContext';
import {
  useParentBridge,
  type PageActionResult,
  type PageActionVerb,
  type ParentPageModel,
} from './ParentBridgeContext';

interface ToastEntry {
  id: string;
  message: string;
}

interface PageContextValue {
  /** Snapshot of the most recent page model the bootstrap relayed. */
  pageModel: ParentPageModel | null;
  /** True when capture is on for this instance. */
  enabled: boolean;
  /** Force a fresh full snapshot. */
  refresh: () => void;
  /**
   * Propose an action against the host page. Mutating verbs (click,
   * set_value, navigate) check auto-grant first; if matched, dispatch
   * immediately and surface a toast. Otherwise returns
   * `{ ok:false, error:'consent_required' }` so the caller can render a
   * confirmation banner.
   */
  proposeAction: (
    verb: PageActionVerb,
    args: Record<string, unknown> & { id?: string },
    reason?: string,
  ) => Promise<PageActionResult>;
  toasts: ToastEntry[];
  dismissToast: (id: string) => void;
}

const READ_ONLY_VERBS: ReadonlySet<PageActionVerb> = new Set([
  'highlight',
  'scroll_into_view',
  'focus',
  'copy_to_clipboard',
  'emit_event',
]);

const PageCtx = createContext<PageContextValue | undefined>(undefined);

function autoGrantMatches(
  verb: PageActionVerb,
  el: Element | null,
  rules: Array<{ verb: string; scope_selector: string }> | undefined,
): boolean {
  if (!rules || rules.length === 0 || !el) return false;
  for (const rule of rules) {
    if (rule.verb !== verb) continue;
    try {
      if (el.matches(rule.scope_selector) || el.closest(rule.scope_selector)) {
        return true;
      }
    } catch {
      // Bad selector configured — skip silently.
    }
  }
  return false;
}

export const PageContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { config } = useInstance();
  const { chatSessionId } = useChatSession();
  const { pageModel, setCapabilities, requestPageModel, dispatchAction } =
    useParentBridge();
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  // Track the version we've already relayed so we don't double-PATCH.
  const lastRelayedVersionRef = useRef<number>(-1);
  const capabilitiesSentRef = useRef<boolean>(false);

  const pageCapture = config?.page_capture;
  const enabled = !!pageCapture?.enabled;

  // Forward capabilities to bootstrap once instance config is resolved.
  // We send even when disabled so the bootstrap knows to tear down any
  // in-flight observer if a partner toggles off mid-session.
  useEffect(() => {
    if (!config) return;
    // pageCapture.enabled is optional in the config schema but required in
    // the bridge contract — coerce to a strict boolean here so the bootstrap
    // sees a definite on/off rather than `undefined`. The cast on `actions`
    // is because partner-config strings widen to `string` in the JSON schema
    // but the bridge types them as the closed `PageActionVerb` union; the
    // dispatcher validates verbs at runtime before honoring them anyway.
    const caps = { ...(pageCapture || {}), enabled } as Parameters<
      typeof setCapabilities
    >[0];
    setCapabilities(caps);
    capabilitiesSentRef.current = true;
  }, [config, pageCapture, enabled, setCapabilities]);

  // Relay each new page model to the backend. Slim summary always; full
  // registry only on `mode === 'full'` since that's the snapshot the
  // backend's page_ctx mirrors. Deltas land in page_history via the
  // summary alone — element-level addressing is whatever the most recent
  // full snapshot contained.
  useEffect(() => {
    if (!enabled) return;
    if (!pageModel) return;
    if (!chatSessionId) return;
    if (pageModel.version_n === lastRelayedVersionRef.current) return;

    // Backend requires url_path on `page` and version_n on `page_registry`
    // (Pydantic validation). Skip the PATCH entirely if either is missing
    // — better to wait for a complete model than to spam 422s. We don't
    // bump lastRelayedVersionRef here so the next observer tick retries.
    const urlPath = pageModel.page?.url_path;
    if (typeof urlPath !== 'string' || urlPath.length === 0) {
      logger.debug('[embed] skip page PATCH: url_path missing', {
        version_n: pageModel.version_n,
      });
      return;
    }

    lastRelayedVersionRef.current = pageModel.version_n;

    const body: Record<string, unknown> = {
      page: {
        url_path: urlPath,
        url_origin: pageModel.page.url_origin,
        title: pageModel.page.title,
        lang: pageModel.page.lang,
        summary_text: pageModel.summary.main_text_excerpt,
        version_n: pageModel.version_n,
        actionable_count: Object.keys(pageModel.elements).length,
      },
    };
    if (pageModel.mode === 'full' && typeof pageModel.version_n === 'number') {
      // Backend expects `regions` as a list (PageRegistryBody.regions: list[dict])
      // but the bridge keeps them keyed by region id for O(1) lookup. Convert
      // here. `elements` stays dict-shaped — backend accepts dict[str, dict].
      const regionsList = Array.isArray(pageModel.regions)
        ? pageModel.regions
        : Object.values(pageModel.regions ?? {});
      body.page_registry = {
        version_n: pageModel.version_n,
        regions: regionsList,
        elements: pageModel.elements,
      };
    }
    httpClient
      .patch(`/sessions/v1/${chatSessionId}`, body)
      .catch((err: unknown) => {
        logger.warn('[embed] page-context PATCH failed', err);
      });
  }, [chatSessionId, enabled, pageModel]);

  const pushToast = useCallback((message: string) => {
    const id = `t_${Math.random().toString(36).slice(2, 8)}_${Date.now().toString(36)}`;
    setToasts(prev => [...prev, { id, message }]);
    window.setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const proposeAction = useCallback<PageContextValue['proposeAction']>(
    async (verb, args, reason) => {
      if (!enabled) return { ok: false, error: 'page_capture_disabled' };
      // Master action toggle — read-only verbs are exempt; mutating verbs
      // require the partner to have actions enabled at all.
      const actionsEnabled = !!pageCapture?.actions?.enabled;
      const isReadOnly = READ_ONLY_VERBS.has(verb);
      if (!isReadOnly && !actionsEnabled) {
        return { ok: false, error: 'mutating_actions_disabled' };
      }

      // Read-only verbs auto-execute. Don't toast — these are non-disruptive.
      if (isReadOnly) {
        return dispatchAction(verb, args);
      }

      // Mutating verbs: check auto-grant. We only know the targeted element
      // by registry id, but the bootstrap is the side that resolves it. To
      // evaluate auto-grant client-side we need the most recent page model
      // to look up the element in the current document — done via
      // querySelector on the parent, which we can't do from inside the
      // iframe. Instead, we lean on the bootstrap to enforce auto-grant
      // when it dispatches by doing this check there. Until that lands,
      // explicit confirmation is required for every mutating verb except
      // when the partner has set `actions.enabled = true` AND the verb
      // isn't in the denied list.
      if ((pageCapture?.actions?.denied || []).includes(verb)) {
        return { ok: false, error: 'verb_denied' };
      }

      // For now: dispatch and surface a toast describing what we did.
      // Inline confirmation banners arrive with the action-proposal UI in
      // a follow-up; this is the auto-grant-by-default path the partner
      // opts into when actions.enabled is true.
      const result = await dispatchAction(verb, args);
      if (result.ok) {
        const label =
          verb === 'set_value'
            ? `WunderGuide filled in a field${reason ? ` — ${reason}` : ''}`
            : verb === 'click'
              ? `WunderGuide clicked an element${reason ? ` — ${reason}` : ''}`
              : verb === 'navigate'
                ? `WunderGuide navigated the page${reason ? ` — ${reason}` : ''}`
                : `WunderGuide ran a page action`;
        pushToast(label);
      }
      return result;
    },
    [dispatchAction, enabled, pageCapture, pushToast],
  );

  const value = useMemo<PageContextValue>(
    () => ({
      pageModel,
      enabled,
      refresh: requestPageModel,
      proposeAction,
      toasts,
      dismissToast,
    }),
    [pageModel, enabled, requestPageModel, proposeAction, toasts, dismissToast],
  );

  return <PageCtx.Provider value={value}>{children}</PageCtx.Provider>;
};

export function usePageContext(): PageContextValue {
  const ctx = useContext(PageCtx);
  if (!ctx)
    throw new Error('usePageContext must be used within PageContextProvider');
  return ctx;
}
